import { useState, useCallback } from 'react';
import { useToast } from './use-toast';

export interface AuthError {
  code: string;
  message: string;
  action?: string;
  retryable?: boolean;
}

export interface AuthErrorState {
  error: AuthError | null;
  isRetrying: boolean;
  retryCount: number;
}

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000; // 1 second

export function useAuthErrors() {
  const { toast } = useToast();
  const [errorState, setErrorState] = useState<AuthErrorState>({
    error: null,
    isRetrying: false,
    retryCount: 0,
  });

  const clearError = useCallback(() => {
    setErrorState({
      error: null,
      isRetrying: false,
      retryCount: 0,
    });
  }, []);

  const handleError = useCallback((error: any, context?: string) => {
    const authError = parseAuthError(error, context);
    
    setErrorState(prev => ({
      error: authError,
      isRetrying: false,
      retryCount: prev.error?.code === authError.code ? prev.retryCount : 0,
    }));

    // Show appropriate toast
    if (authError.action) {
      toast({
        title: getErrorTitle(authError),
        description: authError.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: getErrorTitle(authError),
        description: authError.message,
        variant: "destructive",
      });
    }

    return authError;
  }, [toast]);

  const retryLastOperation = useCallback(async (operation?: () => Promise<any>) => {
    if (!errorState.error?.retryable || errorState.retryCount >= MAX_RETRY_ATTEMPTS) {
      return;
    }

    setErrorState(prev => ({
      ...prev,
      isRetrying: true,
      retryCount: prev.retryCount + 1,
    }));

    // Wait before retrying
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * errorState.retryCount));

    try {
      if (operation) {
        await operation();
        clearError();
        toast({
          title: "Success",
          description: "Operation completed successfully after retry.",
        });
      }
    } catch (error) {
      handleError(error, 'retry');
    } finally {
      setErrorState(prev => ({
        ...prev,
        isRetrying: false,
      }));
    }
  }, [errorState.error, errorState.retryCount, clearError, handleError, toast]);

  return {
    error: errorState.error,
    isRetrying: errorState.isRetrying,
    retryCount: errorState.retryCount,
    handleError,
    clearError,
    retryLastOperation,
    canRetry: errorState.error?.retryable && errorState.retryCount < MAX_RETRY_ATTEMPTS,
  };
}

function parseAuthError(error: any, context?: string): AuthError {
  // Handle Supabase auth errors
  if (error?.message) {
    const message = error.message.toLowerCase();
    
    if (message.includes('invalid login credentials')) {
      return {
        code: 'INVALID_CREDENTIALS',
        message: 'Invalid email or password. Please check your credentials and try again.',
        action: 'Retry',
        retryable: true,
      };
    }
    
    if (message.includes('email not confirmed')) {
      return {
        code: 'EMAIL_NOT_VERIFIED',
        message: 'Please verify your email address before signing in.',
        action: 'Verify Email',
        retryable: false,
      };
    }
    
    if (message.includes('user already registered')) {
      return {
        code: 'USER_EXISTS',
        message: 'An account with this email already exists. Try signing in instead.',
        retryable: false,
      };
    }
    
    if (message.includes('password should be at least')) {
      return {
        code: 'WEAK_PASSWORD',
        message: 'Password must be at least 6 characters long.',
        retryable: true,
      };
    }
    
    if (message.includes('signup is disabled')) {
      return {
        code: 'SIGNUP_DISABLED',
        message: 'New user registration is currently disabled.',
        retryable: false,
      };
    }
    
    if (message.includes('rate limit')) {
      return {
        code: 'RATE_LIMITED',
        message: 'Too many requests. Please wait a moment before trying again.',
        action: 'Retry',
        retryable: true,
      };
    }
    
    if (message.includes('network') || message.includes('fetch')) {
      return {
        code: 'NETWORK_ERROR',
        message: 'Network error. Please check your connection and try again.',
        action: 'Retry',
        retryable: true,
      };
    }
    
    if (message.includes('session') && message.includes('not found')) {
      return {
        code: 'SESSION_EXPIRED',
        message: 'Your session has expired. Please sign in again.',
        retryable: false,
      };
    }
    
    if (message.includes('locked')) {
      return {
        code: 'ACCOUNT_LOCKED',
        message: error.message, // Use the full message which includes unlock time
        retryable: false,
      };
    }
  }
  
  // Handle generic errors
  if (error instanceof Error) {
    return {
      code: 'UNKNOWN_ERROR',
      message: error.message || 'An unexpected error occurred. Please try again.',
      action: 'Retry',
      retryable: true,
    };
  }
  
  // Fallback for unknown error types
  return {
    code: 'UNKNOWN_ERROR',
    message: context 
      ? `An error occurred during ${context}. Please try again.`
      : 'An unexpected error occurred. Please try again.',
    action: 'Retry',
    retryable: true,
  };
}

function getErrorTitle(error: AuthError): string {
  switch (error.code) {
    case 'INVALID_CREDENTIALS':
      return 'Sign In Failed';
    case 'EMAIL_NOT_VERIFIED':
      return 'Email Verification Required';
    case 'USER_EXISTS':
      return 'Account Already Exists';
    case 'WEAK_PASSWORD':
      return 'Password Too Weak';
    case 'SIGNUP_DISABLED':
      return 'Registration Disabled';
    case 'RATE_LIMITED':
      return 'Rate Limited';
    case 'NETWORK_ERROR':
      return 'Connection Error';
    case 'SESSION_EXPIRED':
      return 'Session Expired';
    case 'ACCOUNT_LOCKED':
      return 'Account Locked';
    default:
      return 'Error';
  }
}